/**
 * Async Section Component
 * Wrapper for sections with loading, error, and content states
 *
 * This is the primary component for section-level loading management.
 * It handles all three states (loading, error, success) with appropriate UI.
 */

import type { ReactNode } from 'react';
import { Box } from '@shopify/polaris';
import { LoadingOverlay } from './LoadingOverlay';
import { RetryBanner } from './RetryBanner';

interface AsyncSectionProps {
  /**
   * Whether the section is loading (initial load, no data)
   */
  isLoading: boolean;

  /**
   * Whether the section is refreshing (has data, updating)
   */
  isRefreshing?: boolean;

  /**
   * Whether an error occurred
   */
  isError?: boolean;

  /**
   * Error message to display
   */
  errorMessage?: string;

  /**
   * Callback when retry is clicked
   */
  onRetry?: () => void;

  /**
   * Skeleton placeholder to show during initial load
   */
  skeleton: ReactNode;

  /**
   * Content to render when data is available
   */
  children: ReactNode;

  /**
   * Message to show during refresh
   */
  refreshMessage?: string;

  /**
   * Minimum height to prevent layout shift
   * Useful when skeleton and content have different heights
   */
  minHeight?: string;

  /**
   * Apply content-fade-in animation when loading completes
   * @default true
   */
  animateContent?: boolean;
}

/**
 * Section wrapper with loading state management
 *
 * Features:
 * - Shows skeleton during initial load
 * - Shows overlay spinner during refresh (stale-while-revalidate)
 * - Shows error banner with retry on failure
 * - Smooth content transitions with fade-in animation
 *
 * @example
 * ```tsx
 * <AsyncSection
 *   isLoading={isLoading}
 *   isRefreshing={isRefreshing}
 *   isError={isError}
 *   errorMessage={error?.message}
 *   onRetry={retry}
 *   skeleton={<SkeletonStatCards />}
 * >
 *   <StatisticsCards data={data} />
 * </AsyncSection>
 * ```
 */
export function AsyncSection({
  isLoading,
  isRefreshing = false,
  isError = false,
  errorMessage,
  onRetry,
  skeleton,
  children,
  refreshMessage,
  minHeight,
  animateContent = true,
}: AsyncSectionProps) {
  // Initial loading state - show skeleton with entrance animation
  if (isLoading) {
    return (
      <div
        className="async-section async-section--loading skeleton-enter"
        style={minHeight ? { minHeight } : undefined}
      >
        {skeleton}
      </div>
    );
  }

  // Error state - show retry banner
  if (isError && onRetry) {
    return (
      <div className="async-section async-section--error">
        <RetryBanner
          message={errorMessage}
          onRetry={onRetry}
          isRetrying={isRefreshing}
        />
      </div>
    );
  }

  // Success/refresh state - show content with optional overlay
  return (
    <div
      className="async-section async-section--content"
      style={{
        position: 'relative',
        ...(minHeight ? { minHeight } : {}),
      }}
    >
      <div className={animateContent ? 'content-fade-in' : undefined}>
        {children}
      </div>
      <LoadingOverlay
        isVisible={isRefreshing}
        message={refreshMessage}
      />
    </div>
  );
}

export type { AsyncSectionProps };
